import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Modal } from 'react-bootstrap'
import Select from 'react-select'
import XLSX from 'xlsx'
import { DndContext, DragOverlay, PointerSensor, pointerWithin, rectIntersection, useSensor, useSensors } from '@dnd-kit/core'

import DeleteModal from 'pages/parts/DeleteModal'
import useVirtualRows from 'pages/parts/useVirtualRows'

import { httpOk, GET, POST, PUT, DELETE } from 'helpers/api'
import { notify } from 'helpers/helpers'
import { buildPivot, sortTree, flattenTree, formatMeasure } from './pivot'
import { LEGACY_FILTERS, NON_ADDITIVE, parseId } from './fields'
import FieldList, { fieldSections } from './FieldList'
import Zone from './Zone'
import FilterModal from './FilterModal'

// Конструктор отчётов — как OLAP iiko: слева все поля, их перетаскивают в строки (ось Y),
// колонки (ось X), значения и фильтры, и таблица перестраивается сама.
// Считает сервер тем же движком, что партнёрское API и рассылки — цифры везде одни.

const ROW_HEIGHT = 33
// Колонок в своде больше — таблица перестаёт читаться, просим перенести поле в строки
const MAX_COLUMNS = 60
// Пауза автообновления: человек перетаскивает несколько полей подряд — считаем один раз в конце
const AUTO_REFRESH_DELAY = 700

const DIMENSION_ZONES = ['filters', 'columns', 'rows']

function emptyReport(posId) {
	return {
		id: null,
		name: '',
		report_type: 'SALES',
		layout: { filters: posId ? ['point'] : [], rows: ['product'], columns: [], measures: ['quantity', 'net_amount'] },
		// По умолчанию — торговая точка из шапки; остальные точки — снятием фильтра на поле
		dimension_filters: posId ? { point: [String(posId)] } : {},
		period: { preset: 'this_month' },
	}
}

function readAutoRefresh() {
	try {
		return localStorage.getItem('olap_auto_refresh') !== '0'
	} catch (e) {
		return true
	}
}

function Index() {
	const { t } = useTranslation()
	const reduxAccount = useSelector(state => state.account)

	const [catalog, setCatalog] = useState(null)
	const [presets, setPresets] = useState([])
	const [report, setReport] = useState(() => emptyReport(reduxAccount.pos_id))

	const [result, setResult] = useState(null)
	const [isDirty, setIsDirty] = useState(false)
	const [collapsed, setCollapsed] = useState(new Set())
	const [sort, setSort] = useState(null)
	const [share, setShare] = useState('values')
	const [autoRefresh, setAutoRefresh] = useState(readAutoRefresh)
	const [dragging, setDragging] = useState(null)
	const [filterField, setFilterField] = useState(null)
	const [section, setSection] = useState('all')

	const [modalSave, setModalSave] = useState({ show: false, asNew: false, name: '' })
	const [modalDelete, setModalDelete] = useState(false)

	const buildTimer = useRef(null)
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

	const reportCatalog = useMemo(() => {
		return catalog?.reports?.find(item => item.report_type === report.report_type) || { dimensions: [], measures: [] }
	}, [catalog, report.report_type])

	const dimensionLabel = (name) => t(`olap_dim_${name}`)
	const measureLabel = (name, type = report.report_type) => t(`olap_m_${type}_${name}`)

	async function getCatalog() {
		const response = await GET('/report-olap/columns', {}, { loader: true })
		if (httpOk(response)) setCatalog(response.data)
	}

	async function getPresets() {
		const response = await GET('/report-olap/presets')
		if (httpOk(response)) setPresets(response.data)
	}

	function changeReport(patch) {
		setReport(prev => ({ ...prev, ...patch }))
		setIsDirty(true)
	}

	function changeLayout(layout) {
		changeReport({ layout })
	}

	// --- Раскладка: перетаскивание и двойной клик ---

	function zoneOf(layout, name) {
		return DIMENSION_ZONES.find(zone => layout[zone].includes(name)) || null
	}

	// Поле в зону на позицию index (null — в конец). Разрез живёт только в одной зоне:
	// перенос из строк в колонки убирает его из строк
	function placeField(kind, name, zone, index = null) {
		if (kind === 'measure' && zone !== 'measures') return
		if (kind === 'dim' && zone === 'measures') return

		const layout = { ...report.layout }
		const fromZone = kind === 'measure' ? (layout.measures.includes(name) ? 'measures' : null) : zoneOf(layout, name)

		// Разрезов в строках и колонках вместе — не больше лимита движка
		const maxDimensions = catalog?.max_dimensions || 7
		const grouped = [...layout.rows, ...layout.columns]
		if (kind === 'dim' && ['rows', 'columns'].includes(zone) && !grouped.includes(name) && grouped.length >= maxDimensions) {
			notify(t('olap_max_dims', { count: maxDimensions }))
			return
		}

		let target = [...layout[zone]]
		const currentIndex = target.indexOf(name)
		if (fromZone && fromZone !== zone) {
			layout[fromZone] = layout[fromZone].filter(item => item !== name)
		}
		if (currentIndex !== -1) {
			target.splice(currentIndex, 1)
			if (index !== null && index > currentIndex) index -= 1
		}
		if (index === null || index > target.length) index = target.length
		target.splice(index, 0, name)
		layout[zone] = target
		changeLayout(layout)
	}

	// Убрать поле из отчёта; у разреза снимается и его фильтр
	function removeField(zone, name) {
		const layout = { ...report.layout, [zone]: report.layout[zone].filter(item => item !== name) }
		if (zone !== 'measures') {
			const dimensionFilters = { ...report.dimension_filters }
			delete dimensionFilters[name]
			changeReport({ layout, dimension_filters: dimensionFilters })
			return
		}
		changeLayout(layout)
	}

	function addByDoubleClick(kind, name) {
		if (kind === 'measure') {
			if (!report.layout.measures.includes(name)) placeField('measure', name, 'measures')
			return
		}
		if (!zoneOf(report.layout, name)) placeField('dim', name, 'rows')
	}

	function handleDragStart(event) {
		const [source, kind, name] = parseId(event.active.id)
		const realKind = source === 'chip' ? (kind === 'measures' ? 'measure' : 'dim') : kind
		setDragging({ kind: realKind, name })
	}

	function handleDragEnd(event) {
		setDragging(null)
		const { active, over } = event
		if (!over) return

		const [source, sourceKind, name] = parseId(active.id)
		const kind = source === 'chip' ? (sourceKind === 'measures' ? 'measure' : 'dim') : sourceKind
		const [target, zone, slot] = parseId(over.id)

		if (target === 'trash') {
			if (source === 'chip') removeField(sourceKind, name)
			return
		}
		if (target === 'zone') placeField(kind, name, zone)
		if (target === 'slot') placeField(kind, name, zone, Number(slot))
	}

	// --- Фильтры по значениям ---

	function applyFilter(name, values) {
		const dimensionFilters = { ...report.dimension_filters }
		if (values.length > 0) {
			dimensionFilters[name] = values
		} else {
			delete dimensionFilters[name]
		}
		// Отфильтрованное поле, которого нет ни в одной зоне, кладём в «Фильтры» — чтобы фильтр было видно
		const layout = { ...report.layout }
		if (values.length > 0 && !zoneOf(layout, name)) {
			layout.filters = [...layout.filters, name]
		}
		changeReport({ dimension_filters: dimensionFilters, layout })
		setFilterField(null)
	}

	const filterCounts = useMemo(() => {
		const counts = {}
		Object.entries(report.dimension_filters || {}).forEach(([name, values]) => { counts[name] = values.length })
		return counts
	}, [report.dimension_filters])

	// --- Вид отчёта ---

	// Смена вида: поля и фильтры, которых у нового вида нет, убираем
	function changeReportType(type) {
		const next = catalog.reports.find(item => item.report_type === type)
		const keep = list => list.filter(name => next.dimensions.includes(name))
		let measures = report.layout.measures.filter(name => next.measures.includes(name))
		if (measures.length === 0) measures = [next.measures[0]]

		const dimensionFilters = {}
		Object.entries(report.dimension_filters || {}).forEach(([name, values]) => {
			if (next.dimensions.includes(name)) dimensionFilters[name] = values
		})

		changeReport({
			report_type: type,
			layout: { filters: keep(report.layout.filters), rows: keep(report.layout.rows), columns: keep(report.layout.columns), measures },
			dimension_filters: dimensionFilters,
		})
	}

	function requestBody() {
		return {
			report_type: report.report_type,
			layout: report.layout,
			filters: {},
			dimension_filters: report.dimension_filters,
			period: report.period,
		}
	}

	const canBuild = report.layout.measures.length > 0
		&& (report.period.preset !== 'custom' || (report.period.from && report.period.to))

	async function build() {
		if (!canBuild) return
		const response = await POST('/report-olap/run', requestBody(), { loader: true, notification: false })
		if (httpOk(response)) {
			setResult(response.data)
			setCollapsed(new Set())
			setSort(null)
			setIsDirty(false)
		}
	}

	// Автообновление: любое изменение раскладки, фильтра или периода — пересчёт после паузы
	const requestKey = JSON.stringify(requestBody())
	useEffect(() => {
		if (!autoRefresh || !catalog || !canBuild) return
		if (buildTimer.current) clearTimeout(buildTimer.current)
		buildTimer.current = setTimeout(() => build(), AUTO_REFRESH_DELAY)
		return () => clearTimeout(buildTimer.current)
	}, [requestKey, autoRefresh, catalog]) // eslint-disable-line react-hooks/exhaustive-deps

	function toggleAutoRefresh(bool) {
		setAutoRefresh(bool)
		try {
			localStorage.setItem('olap_auto_refresh', bool ? '1' : '0')
		} catch (e) {
			// без localStorage просто не запоминаем
		}
	}

	// --- Сохранённые отчёты ---

	function openPreset(preset) {
		if (!preset) {
			setReport(emptyReport(reduxAccount.pos_id))
			setResult(null)
			setIsDirty(false)
			return
		}
		const filters = preset.filters || {}
		const dimensionFilters = { ...(filters.dimension_filters || {}) }
		// Отчёты, сохранённые до перетаскивания: фильтры списками → фильтры на полях
		Object.entries(LEGACY_FILTERS).forEach(([key, name]) => {
			if (Array.isArray(filters[key]) && filters[key].length > 0 && !dimensionFilters[name]) {
				dimensionFilters[name] = filters[key].map(String)
			}
		})
		const layout = {
			filters: preset.layout?.filters || [],
			rows: preset.layout?.rows || [],
			columns: preset.layout?.columns || [],
			measures: preset.layout?.measures || [],
		}
		Object.keys(dimensionFilters).forEach(name => {
			if (!zoneOf(layout, name)) layout.filters = [...layout.filters, name]
		})
		setReport({
			id: preset.id,
			name: preset.name,
			report_type: preset.report_type,
			layout,
			dimension_filters: dimensionFilters,
			period: preset.period || { preset: 'yesterday' },
		})
		setResult(null)
		setIsDirty(true)
	}

	async function savePreset(e) {
		e.preventDefault()
		const body = { ...requestBody(), name: modalSave.name }
		var response
		if (report.id && !modalSave.asNew) {
			response = await PUT(`/report-olap/presets/${report.id}`, body, { loader: true })
		} else {
			response = await POST('/report-olap/presets', body, { loader: true })
		}
		if (httpOk(response)) {
			setReport(prev => ({ ...prev, id: response.data.id, name: modalSave.name }))
			setModalSave({ show: false, asNew: false, name: '' })
			getPresets()
		}
	}

	async function deletePreset() {
		const response = await DELETE(`/report-olap/presets/${report.id}`, {}, { loader: true })
		if (httpOk(response)) {
			setModalDelete(false)
			openPreset(null)
			getPresets()
		}
	}

	// --- Сводная таблица ---

	// Свод строим по раскладке, с которой отчёт посчитан, а не по текущей правке
	const pivot = useMemo(() => {
		if (!result) return null
		const model = buildPivot(result.rows || [], result.layout, t)
		sortTree(model.root, model, sort)
		return model
	}, [result, sort]) // eslint-disable-line react-hooks/exhaustive-deps

	const resultRows = result?.layout?.rows || []
	const resultColumns = result?.layout?.columns || []
	const resultMeasures = result?.layout?.measures || []
	const resultType = result?.report_type

	const displayRows = useMemo(() => {
		if (!pivot) return []
		const list = flattenTree(pivot.root, collapsed, resultRows.length)
		list.push({ node: pivot.root, hasChildren: false, isTotal: true })
		return list
	}, [pivot, collapsed]) // eslint-disable-line react-hooks/exhaustive-deps

	const tooManyColumns = pivot && pivot.columns.length > MAX_COLUMNS
	const virtual = useVirtualRows({ count: displayRows.length, rowHeight: ROW_HEIGHT })

	function toggleNode(key) {
		const next = new Set(collapsed)
		next.has(key) ? next.delete(key) : next.add(key)
		setCollapsed(next)
	}

	function collapseAll(bool) {
		if (!bool) {
			setCollapsed(new Set())
			return
		}
		const next = new Set()
		const walk = node => node.children.forEach(child => {
			if (child.children.length > 0) next.add(child.key)
			walk(child)
		})
		walk(pivot.root)
		setCollapsed(next)
	}

	function toggleSort(measure) {
		if (!sort || sort.measure !== measure) {
			setSort({ measure, direction: 'desc' })
		} else if (sort.direction === 'desc') {
			setSort({ measure, direction: 'asc' })
		} else {
			setSort(null)
		}
	}

	// Колонки значений: по каждой колонке свода — все показатели, затем итог строки
	const valueColumns = useMemo(() => {
		if (!pivot) return []
		const list = []
		if (resultColumns.length > 0) {
			pivot.columns.forEach(column => resultMeasures.forEach(measure => list.push({ columnKey: column.key, measure })))
		}
		resultMeasures.forEach(measure => list.push({ columnKey: '__total', measure, isTotal: true }))
		return list
	}, [pivot]) // eslint-disable-line react-hooks/exhaustive-deps

	// Значение ячейки с учётом режима показа: само значение или доля от итога строки / колонки / отчёта
	function cellValue(node, columnKey, measure) {
		const value = pivot.cell(node, columnKey)?.[measure]
		if (share === 'values' || NON_ADDITIVE.includes(measure) || value === null || value === undefined) {
			return { value, isShare: false }
		}
		let base = null
		if (share === 'row') base = pivot.cell(node, '__total')?.[measure]
		if (share === 'column') base = pivot.cell(pivot.root, columnKey)?.[measure]
		if (share === 'total') base = pivot.cell(pivot.root, '__total')?.[measure]
		if (!base) return { value: null, isShare: true }
		return { value: Number(value) * 100 / Number(base), isShare: true }
	}

	function formatCell(measure, cell) {
		if (cell.value === null || cell.value === undefined) return ''
		return cell.isShare ? `${Number(cell.value).toFixed(1)}%` : formatMeasure(measure, cell.value)
	}

	function exportToExcel() {
		if (!pivot) return
		const rowHeaders = resultRows.length > 0 ? resultRows.map(name => dimensionLabel(name)) : ['']
		const header = [...rowHeaders]
		const header2 = rowHeaders.map(() => '')
		if (resultColumns.length > 0) {
			pivot.columns.forEach(column => resultMeasures.forEach((measure, index) => {
				header.push(index === 0 ? column.labels.join(' / ') : '')
				header2.push(measureLabel(measure, resultType))
			}))
		}
		resultMeasures.forEach((measure, index) => {
			header.push(index === 0 ? t('olap_total') : '')
			header2.push(measureLabel(measure, resultType))
		})

		const sheet = [
			[report.name || t('olap_report'), `${result.date_from} — ${result.date_to}`, share !== 'values' ? t(`olap_show_${share}`) : ''],
			[],
			header,
			header2,
		]
		// В файл — всё дерево целиком, независимо от свёрнутых групп
		const all = flattenTree(pivot.root, new Set(), resultRows.length)
		all.push({ node: pivot.root, isTotal: true })
		all.forEach(({ node, isTotal }) => {
			const line = rowHeaders.map((_, index) => {
				if (isTotal) return index === 0 ? t('olap_total') : ''
				return index < node.level ? node.labels[index] : (index === node.level ? t('olap_total') : '')
			})
			valueColumns.forEach(column => {
				const cell = cellValue(node, column.columnKey, column.measure)
				line.push(cell.value === null || cell.value === undefined ? '' : Math.round(Number(cell.value) * 100) / 100)
			})
			sheet.push(line)
		})

		const ws = XLSX.utils.aoa_to_sheet(sheet)
		const wb = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(wb, ws, 'OLAP')
		XLSX.writeFile(wb, `${report.name || t('olap_report')}.xlsx`)
	}

	useEffect(() => {
		getCatalog()
		getPresets()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const selectedPreset = presets.find(item => item.id === report.id) || null
	const periodPresets = catalog?.period_presets || []
	const usedFields = [
		...[...report.layout.filters, ...report.layout.rows, ...report.layout.columns].map(name => 'dim:' + name),
		...report.layout.measures.map(name => 'measure:' + name),
	]

	return (
		<>
			{/* Верхняя панель — как шапка OLAP iiko: формат отчёта, сохранение, период, обновление */}
			<div className="olap-toolbar">
				<div className="olap-toolbar__row">
					<h4 className="olap-toolbar__title">{t('olap_report')}</h4>
					<div style={{ minWidth: '260px' }}>
						<Select
							placeholder={t('olap_saved_reports')}
							options={presets}
							value={selectedPreset}
							onChange={(option) => openPreset(option)}
							getOptionLabel={(option) => option.name}
							getOptionValue={(option) => option.id}
							isClearable
						/>
					</div>
					<button className="btn btn-sm btn-outline-primary" onClick={() => openPreset(null)}>
						<i className="uil uil-plus me-1"></i>{t('olap_new_report')}
					</button>
					<button className="btn btn-sm btn-outline-primary"
						onClick={() => setModalSave({ show: true, asNew: false, name: report.name })}>
						<i className="uil uil-save me-1"></i>{t('save')}
					</button>
					{report.id &&
						<>
							<button className="btn btn-sm btn-outline-primary"
								onClick={() => setModalSave({ show: true, asNew: true, name: '' })}>
								{t('olap_save_as')}
							</button>
							<button className="btn btn-sm btn-outline-danger" onClick={() => setModalDelete(true)}>
								<i className="uil uil-trash-alt"></i>
							</button>
						</>
					}
					<div className="ms-auto d-flex align-items-center gap-2">
						{result &&
							<button className="btn btn-sm btn-outline-success" onClick={exportToExcel} disabled={tooManyColumns}>
								<i className="uil uil-file-download-alt me-1"></i>Excel
							</button>
						}
						<button className="btn btn-sm btn-primary" onClick={build} disabled={!canBuild}>
							<i className="uil uil-sync me-1"></i>{t('olap_refresh')}
						</button>
					</div>
				</div>

				<div className="olap-toolbar__row">
					<div className="btn-group">
						{(catalog?.reports || []).map(item => (
							<button key={item.report_type} type="button"
								className={`btn btn-sm ${report.report_type === item.report_type ? 'btn-primary' : 'btn-outline-primary'}`}
								onClick={() => report.report_type !== item.report_type && changeReportType(item.report_type)}>
								{t(`olap_type_${item.report_type}`)}
							</button>
						))}
					</div>

					<span className="olap-toolbar__label">{t('period')}</span>
					<select className="form-select form-select-sm" style={{ width: 'auto' }} value={report.period.preset}
						onChange={(e) => changeReport({ period: { ...report.period, preset: e.target.value } })}>
						{periodPresets.map(preset => (
							<option key={preset} value={preset}>{t(`olap_period_${preset}`)}</option>
						))}
					</select>
					{report.period.preset === 'custom' &&
						<>
							<input type="date" className="form-control form-control-sm" style={{ width: 'auto' }} value={report.period.from || ''}
								onChange={(e) => changeReport({ period: { ...report.period, from: e.target.value } })} />
							<input type="date" className="form-control form-control-sm" style={{ width: 'auto' }} value={report.period.to || ''}
								onChange={(e) => changeReport({ period: { ...report.period, to: e.target.value } })} />
							<small className="text-muted text-nowrap">{t('olap_period_max', { days: catalog?.max_period_days || 92 })}</small>
						</>
					}
					{result &&
						<span className="text-muted small">
							{result.date_from.split('-').reverse().join('.')} — {result.date_to.split('-').reverse().join('.')}
						</span>
					}

					<div className="ms-auto d-flex align-items-center gap-3">
						<select className="form-select form-select-sm" style={{ width: 'auto' }} value={share}
							onChange={(e) => setShare(e.target.value)} title={t('olap_show_hint')}>
							{['values', 'row', 'column', 'total'].map(mode => (
								<option key={mode} value={mode}>{t(`olap_show_${mode}`)}</option>
							))}
						</select>
						<div className="form-check mb-0">
							<input type="checkbox" className="form-check-input" id="olapAutoRefresh"
								checked={autoRefresh} onChange={(e) => toggleAutoRefresh(e.target.checked)} />
							<label className="form-check-label" htmlFor="olapAutoRefresh">{t('olap_auto_refresh')}</label>
						</div>
					</div>
				</div>
			</div>

			<DndContext sensors={sensors}
				collisionDetection={(args) => {
					const hits = pointerWithin(args)
					return hits.length > 0 ? hits : rectIntersection(args)
				}}
				onDragStart={handleDragStart}
				onDragCancel={() => setDragging(null)}
				onDragEnd={handleDragEnd}>

				{/* Разделы полей — одной строкой, как кнопки «Блюда / Время / Заказ…» в iiko */}
				<div className="olap-sections">
					{fieldSections(reportCatalog.dimensions).map(key => (
						<button key={key} type="button"
							className={`olap-sections__item ${section === key ? 'olap-sections__item--active' : ''}`}
							onClick={() => setSection(key)}>
							{t(`olap_section_${key}`)}
						</button>
					))}
				</div>

				<div className="olap-builder">
					<div className="olap-builder__fields">
						<FieldList
							dimensions={reportCatalog.dimensions}
							measures={reportCatalog.measures}
							usedFields={usedFields}
							section={section}
							dimensionLabel={dimensionLabel}
							measureLabel={(name) => measureLabel(name)}
							onAdd={addByDoubleClick} />
					</div>

					<div className="olap-builder__main">
						<Zone zone="filters" title={t('olap_zone_filters')} icon="uil-filter" className="olap-zone--filters"
							fields={report.layout.filters} label={dimensionLabel}
							filterCounts={filterCounts} onFilter={setFilterField} onRemove={removeField}
							hint={t('olap_zone_filters_hint')} />

						{/* Шапка сводной таблицы — сами зоны: слева сверху показатели, справа колонки (ось X),
							под ними заголовки строк (ось Y) */}
						<div className="olap-grid-head">
							<Zone zone="measures" title={t('olap_zone_values')} className="olap-zone--values"
								fields={report.layout.measures} label={(name) => measureLabel(name)}
								onRemove={removeField}
								hint={t('olap_zone_values_hint')} />
							<Zone zone="columns" title={t('olap_zone_columns')} className="olap-zone--columns"
								fields={report.layout.columns} label={dimensionLabel}
								filterCounts={filterCounts} onFilter={setFilterField} onRemove={removeField}
								hint={t('olap_zone_columns_hint')} />
							<Zone zone="rows" title={t('olap_zone_rows')} className="olap-zone--rows"
								fields={report.layout.rows} label={dimensionLabel}
								filterCounts={filterCounts} onFilter={setFilterField} onRemove={removeField}
								hint={t('olap_zone_rows_hint')} />
							<div className="olap-grid-head__tools">
								{result && resultRows.length > 1 &&
									<>
										<button className="btn btn-sm btn-link p-0" onClick={() => collapseAll(false)}>{t('olap_expand_all')}</button>
										<button className="btn btn-sm btn-link p-0" onClick={() => collapseAll(true)}>{t('olap_collapse_all')}</button>
									</>
								}
								{isDirty && !autoRefresh && result &&
									<span className="text-warning small">
										<i className="uil uil-info-circle me-1"></i>{t('olap_changed_hint')}
									</span>
								}
							</div>
						</div>

						<div className="olap-grid">
								{!result &&
									<div className="olap-grid__empty">{t('olap_hint_drag')}</div>
								}

								{result && tooManyColumns &&
									<div className="alert alert-warning mb-0">{t('olap_too_many_columns', { count: pivot.columns.length })}</div>
								}

								{result && !tooManyColumns &&
									<div className="table-responsive table-fixed" ref={virtual.containerRef} style={{ maxHeight: 'calc(100vh - 260px)' }}>
										<table className="table table-bordered table-sm mb-0" style={{ whiteSpace: 'nowrap' }}>
											<thead>
												<tr>
													<th rowSpan={resultColumns.length > 0 ? 2 : 1} style={{ minWidth: '240px' }}>
														{resultRows.map(name => dimensionLabel(name)).join(' / ')}
													</th>
													{resultColumns.length > 0 && pivot.columns.map(column => (
														<th key={column.key} colSpan={resultMeasures.length} className="text-center">
															{column.labels.join(' / ')}
														</th>
													))}
													{resultColumns.length > 0 &&
														<th colSpan={resultMeasures.length} className="text-center">{t('olap_total')}</th>
													}
													{resultColumns.length === 0 && resultMeasures.map(measure => (
														<th key={measure} className="text-end cursor" onClick={() => toggleSort(measure)}>
															{measureLabel(measure, resultType)}
															{sort?.measure === measure && <i className={`uil ${sort.direction === 'desc' ? 'uil-arrow-down' : 'uil-arrow-up'} ms-1`}></i>}
														</th>
													))}
												</tr>
												{resultColumns.length > 0 &&
													<tr>
														{valueColumns.map((column, index) => (
															<th key={index} className={`text-end ${column.isTotal ? 'cursor' : ''}`}
																onClick={() => column.isTotal && toggleSort(column.measure)}>
																{measureLabel(column.measure, resultType)}
																{column.isTotal && sort?.measure === column.measure &&
																	<i className={`uil ${sort.direction === 'desc' ? 'uil-arrow-down' : 'uil-arrow-up'} ms-1`}></i>
																}
															</th>
														))}
													</tr>
												}
											</thead>
											<tbody>
												{virtual.topSpacer > 0 && <tr style={{ height: virtual.topSpacer }} />}
												{displayRows.slice(virtual.start, virtual.end).map(({ node, hasChildren, isTotal }) => {
													const isGroup = hasChildren || isTotal
													return (
														<tr key={isTotal ? '__grand_total' : node.key} style={{ height: ROW_HEIGHT }}
															className={isTotal ? 'table-secondary fw-bold' : (isGroup ? 'fw-semibold' : '')}>
															<td style={{ paddingLeft: `${8 + Math.max(node.level - 1, 0) * 18}px` }}
																className={hasChildren ? 'cursor' : ''}
																onClick={() => hasChildren && toggleNode(node.key)}>
																{hasChildren &&
																	<i className={`uil ${collapsed.has(node.key) ? 'uil-angle-right' : 'uil-angle-down'} me-1`}></i>
																}
																{node.label}
															</td>
															{valueColumns.map((column, index) => (
																<td key={index} className={`text-end ${column.isTotal && resultColumns.length > 0 ? 'fw-semibold' : ''}`}>
																	{formatCell(column.measure, cellValue(node, column.columnKey, column.measure))}
																</td>
															))}
														</tr>
													)
												})}
												{virtual.bottomSpacer > 0 && <tr style={{ height: virtual.bottomSpacer }} />}
											</tbody>
										</table>
									</div>
								}
						</div>
					</div>
				</div>

				<DragOverlay dropAnimation={null}>
					{dragging &&
						<span className="olap-chip olap-chip--overlay">
							{dragging.kind === 'measure' ? measureLabel(dragging.name) : dimensionLabel(dragging.name)}
						</span>
					}
				</DragOverlay>
			</DndContext>

			{filterField &&
				<FilterModal
					field={filterField}
					title={dimensionLabel(filterField)}
					request={requestBody()}
					selected={report.dimension_filters?.[filterField] || []}
					measureLabel={(name) => measureLabel(name)}
					onApply={(values) => applyFilter(filterField, values)}
					onClose={() => setFilterField(null)} />
			}

			{/* MODAL SAVE */}
			<Modal show={modalSave.show} animation={false} centered backdrop="static"
				onHide={() => setModalSave({ show: false, asNew: false, name: '' })}>
				<Modal.Header closeButton>
					<Modal.Title>{modalSave.asNew ? t('olap_save_as') : t('save')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form onSubmit={savePreset} autoComplete="off">
						<div className="form-group">
							<label>{t('olap_report_name')}<span className="required-mark">*</span></label>
							<input type="text" className="form-control" autoFocus maxLength={150}
								value={modalSave.name}
								onChange={(e) => setModalSave({ ...modalSave, name: e.target.value })} />
						</div>
						<div className="d-flex gap-2 w-100 mt-3">
							<button type="button" className="btn btn-outline-warning w-100"
								onClick={() => setModalSave({ show: false, asNew: false, name: '' })}>
								{t('cancel')}
							</button>
							<button type="submit" className="btn btn-primary w-100" disabled={!modalSave.name.trim()}>
								{t('save')}
							</button>
						</div>
					</form>
				</Modal.Body>
			</Modal>
			{/* MODAL SAVE */}

			<DeleteModal
				modalDelete={modalDelete}
				setModalDelete={setModalDelete}
				deleteItem={deletePreset}
				message={t('olap_delete_confirm', { name: report.name })} />
		</>
	)
}

export default Index
